import { ArrowRight, BookOpen, FileText, Rocket, Server, Zap } from "lucide-react";
import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { siteHeaderActionsEn, siteNavItemsEn } from "@/app/content-en";

const docsSections = [
  {
    title: "Quick start paths",
    icon: <Rocket className="h-6 w-6" strokeWidth={3} />,
    accent: "#A3E635",
    body:
      "These English quick starts now cover the first runnable Caller and Responder loops with real commands, result shapes, and the minimum contract fields you need to inspect.",
    links: [
      { href: "/en/docs/quick-start-caller/", label: "Caller Quick Start" },
      { href: "/en/docs/quick-start-responder/", label: "Responder Quick Start" },
    ],
  },
  {
    title: "Protocol docs",
    icon: <FileText className="h-6 w-6" strokeWidth={3} />,
    accent: "#3B82F6",
    body:
      "The protocol section defines how identity, routing, settlement, and result packaging stay stable across many operator-owned capabilities, including the control-plane and data-plane split.",
    links: [
      { href: "/en/docs/protocol-architecture/", label: "Protocol Architecture" },
      { href: "/en/glossary/", label: "Glossary" },
      { href: "/en/docs/protocol-examples/", label: "Protocol Examples" },
      { href: "/en/compare/hotline-vs-api/", label: "Hotline vs API" },
    ],
  },
  {
    title: "Platform and marketplace",
    icon: <Server className="h-6 w-6" strokeWidth={3} />,
    accent: "#8B5CF6",
    body:
      "Marketplace is the public discovery surface. Platform and Selfhost are the deployment profiles that decide where the control plane, console, edge routes, and safety checks live.",
    links: [
      { href: "/en/docs/deployability-profiles/", label: "Deployability Profiles" },
      { href: "/marketplace", label: "Marketplace" },
      { href: "/en/faq/", label: "FAQ" },
    ],
  },
  {
    title: "Why Hotline exists",
    icon: <Zap className="h-6 w-6" strokeWidth={3} />,
    accent: "#F97316",
    body:
      "The compare pages explain why the project is not reducible to MCP, Skills, SaaS, or raw APIs. They are the fastest route to the product model.",
    links: [
      { href: "/en/compare/hotline-vs-mcp/", label: "Hotline vs MCP" },
      { href: "/en/compare/hotline-vs-skills/", label: "Hotline vs Skills" },
      { href: "/en/compare/hotline-vs-saas/", label: "Hotline vs SaaS" },
    ],
  },
  {
    title: "Evidence pages",
    icon: <BookOpen className="h-6 w-6" strokeWidth={3} />,
    accent: "#14B8A6",
    body:
      "These case studies are intentionally non-fictional. They document the real caller-side and responder-side proof paths the current public stack can demonstrate without inventing customer logos or fake metrics.",
    links: [
      { href: "/en/case-studies/caller-team-local-loop/", label: "Caller Case Study" },
      { href: "/en/case-studies/responder-operator-first-hotline/", label: "Responder Case Study" },
    ],
  },
];

export default function EnglishDocsPage() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItemsEn} actionItems={siteHeaderActionsEn} homeHref="/en/" />

      <ContentHero
        eyebrow="DOCS · ENGLISH"
        eyebrowColor="#A3E635"
        titleTop="Docs overview"
        titleBottom="for CALL ANYTHING"
        bottomColor="#A3E635"
        lead="This page is the English entrypoint for understanding the protocol, capability model, and discovery surfaces. It does not yet mirror every Chinese implementation guide one-to-one."
        meta={["English docs index", "Quick start pointers", "Protocol context"]}
        breadcrumbs={[
          { label: "English Home", href: "/en/" },
          { label: "Docs" },
        ]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid gap-5 lg:grid-cols-2">
          {docsSections.map((section) => (
            <article
              key={section.title}
              className="border-[6px] border-black bg-white/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8"
            >
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-12 w-12 items-center justify-center border-4 border-black"
                  style={{ backgroundColor: section.accent }}
                >
                  {section.icon}
                </span>
                <h2 className="text-2xl font-black uppercase text-black">{section.title}</h2>
              </div>
              <p className="mt-4 text-sm font-bold leading-7 text-zinc-800 sm:text-base">{section.body}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                {section.links.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-2 border-4 border-black bg-[#F8F6EF] px-4 py-2 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
                  >
                    <BookOpen className="h-4 w-4" strokeWidth={3} />
                    {link.label}
                  </a>
                ))}
              </div>
            </article>
          ))}
        </div>

        <aside className="mt-12 border-[6px] border-black bg-black p-6 text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#A3E635]">CURRENT STATE</p>
          <h2 className="mt-3 text-2xl font-black uppercase sm:text-3xl">English quick starts are now live</h2>
          <p className="mt-4 text-sm font-bold leading-7 text-zinc-300 sm:text-base">
            The concept pages still define the product model, but implementation-intent queries can now land on English quick starts with real commands, request flow, result_package samples, and review payload examples.
          </p>
          <a
            href="/en/docs/quick-start-caller/"
            className="mt-6 inline-flex items-center gap-2 border-4 border-black bg-[#A3E635] px-5 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
          >
            Open Caller Quick Start
            <ArrowRight className="h-4 w-4" strokeWidth={3} />
          </a>
        </aside>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
